<template>
  <SoDialog
    v-model="isOpen"
    :loading="userCreateMutator.isPending"
    card-title="Create Online Account"
    max-width="500"
    data-testid="add-user-modal"
  >
    <SoErrorBoundary>
      <SoForm data-testid="add-user-form" @submit="handleSubmit()">
        <SoTextField
          v-model="accountNumber"
          :rules="[rules.accountNumber]"
          label="Account number"
          placeholder="01234567"
          type="number"
          data-testid="add-user-txt-field"
          name="account-number"
        />
        <SoCheckbox
          v-model="skipEmail"
          label="Do not send password instructions email"
          class="mt-2"
          data-testid="add-user-checkbox"
        />
        <SoAlert
          v-if="error"
          :text="error"
          title="Error"
          type="error"
          class="mt-4"
          data-testid="add-user-error-alert"
        />
        <div class="d-flex justify-end mt-8">
          <SoButton
            type="success"
            title="Create user"
            large
            submit
            :loading="userCreateMutator.isPending"
            data-testid="add-user-create-btn"
          />
        </div>
      </SoForm>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"

  import { useSnackbarStore } from "@/stores/snackbar"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import rules from "@/validators/rules"
  import SoAlert from "@/components/SoAlert.vue"
  import { UserMutator } from "@/mutators/user/UserMutator"
  import { CreateUserFromJuniferResponse } from "@/api/backofficeRest/generated/model"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoForm,
      SoButton,
      SoTextField,
      SoCheckbox,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        accountNumber: "",
        skipEmail: false,
        userCreateMutator: UserMutator.createFromJunifer(),
        error: "",
        rules,
      }
    },

    computed: {
      isOpen: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },

    watch: {
      isOpen(val) {
        if (val) this.resetForm()
      },
    },

    methods: {
      async handleSubmit() {
        this.error = ""

        try {
          const { mutateAsync } = this.userCreateMutator

          const response = (await mutateAsync({
            accountNumber: this.accountNumber,
            skipEmail: this.skipEmail,
          })) as CreateUserFromJuniferResponse

          this.snackbar.addSuccess(
            `${response.email} was successfully created!`,
          )
          this.isOpen = false
        } catch (error) {
          console.error(error)
          errorReporter.report(error)
          this.error = this.mapError(error)
        } finally {
          this.userCreateMutator.reset()
        }
      },
      mapError(e) {
        const error = errorHandler.userMessage(e, "Something went wrong")

        if (error.includes("Failed to create user for email null")) {
          return `
            The account does not have account contact with email.
            Please make sure to first create the primary contact with valid email address.
          `
        }

        return error
      },
      resetForm() {
        this.error = ""
        this.accountNumber = ""
        this.skipEmail = false
      },
    },
  })
</script>
